import React, { useState, useRef, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import Webcam from 'react-webcam'
import {
  FiShield,
  FiCheckCircle,
  FiInfo,
  FiArrowRight,
  FiArrowLeft,
  FiLoader,
  FiAlertCircle,
  FiUser,
  FiFileText,
  FiCamera,
} from 'react-icons/fi'
import { useSelector } from 'react-redux'
import {
  useSubmitNigerianKycMutation,
  NigerianKycResponse,
} from '../../../features/account/kycSlice'
import { selectCurrentUser } from '../../../features/auth/authSlice'

// ============================================
// Types
// ============================================

interface Props {
  setCurrentStep: React.Dispatch<
    React.SetStateAction<'country' | 'init' | 'nigerian' | 'Success' | 'Verified'>
  >
}

type KycStep = 'intro' | 'idForm' | 'selfie' | 'submitting' | 'success' | 'error'

interface IdFormData {
  documentType: string
  idNumber: string
  firstName: string
  lastName: string
  dateOfBirth: string
  address: string
  city: string
  postalCode: string
  placeOfBirth: string
  consent: boolean
}

// Document types with ID format hints
// Task-list 5.0 T10 (owner's item 4): NIN or BVN only for new submissions.
const DOCUMENT_TYPES = [
  { value: 'nin', label: 'NIN (National Identification Number)', hint: '11 digits', pattern: /^\d{11}$/ },
  { value: 'bvn', label: 'BVN (Bank Verification Number)', hint: '11 digits', pattern: /^\d{11}$/ },
]

// ============================================
// Component
// ============================================

export const NigerianKycForm: React.FC<Props> = ({ setCurrentStep }) => {
  const [submitNigerianKyc] = useSubmitNigerianKycMutation()
  const user = useSelector(selectCurrentUser)

  const sandboxMode = import.meta.env.VITE_YOUVERIFY_SANDBOX === 'true'

  // Step state
  const [step, setStep] = useState<KycStep>('intro')

  // Form data. ?doc=bvn preselects the BVN — the bank-deposit page sends
  // un-verified users here, and a BVN KYC is what a virtual account needs.
  const [formData, setFormData] = useState<IdFormData>({
    documentType:
      new URLSearchParams(window.location.search).get('doc') === 'bvn' ? 'bvn' : '',
    idNumber: '',
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    dateOfBirth: '',
    address: '',
    city: '',
    postalCode: '',
    placeOfBirth: '',
    consent: false,
  })

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof IdFormData, string>>>({})

  // Verification result
  const [verificationResult, setVerificationResult] = useState<NigerianKycResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Selfie state
  const [selfieImage, setSelfieImage] = useState<string | null>(null) // preview URL
  const [selfieFile, setSelfieFile] = useState<File | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const webcamRef = useRef<Webcam>(null)

  // ============================================
  // Form Handlers
  // ============================================

  const handleInputChange = (field: keyof IdFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validateIdForm = (): boolean => {
    const newErrors: Partial<Record<keyof IdFormData, string>> = {}

    if (!formData.documentType) {
      newErrors.documentType = 'Please select a document type'
    }

    if (!formData.idNumber.trim()) {
      newErrors.idNumber = 'Please enter your ID number'
    } else {
      const docType = DOCUMENT_TYPES.find(d => d.value === formData.documentType)
      if (docType && !docType.pattern.test(formData.idNumber.trim())) {
        newErrors.idNumber = `Invalid format. Expected: ${docType.hint}`
      }
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Surname is required'
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    }

    if (!formData.consent) {
      newErrors.consent = 'You must consent to verification'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleContinueToSelfie = () => {
    if (validateIdForm()) {
      setStep('selfie')
    }
  }

  // ============================================
  // Selfie Handlers
  // ============================================

  const startCamera = () => setShowCamera(true)
  const stopCamera = () => setShowCamera(false)

  const captureSelfie = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot()
      if (imageSrc) {
        fetch(imageSrc)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' })
            setSelfieFile(file)
            setSelfieImage(imageSrc)
            stopCamera()
            toast.success('Selfie captured successfully!')
          })
          .catch(error => {
            console.error('Error processing image:', error)
            toast.error('Failed to capture selfie')
          })
      } else {
        toast.error('Camera not ready. Please try again.')
      }
    } else {
      toast.error('Camera not ready. Please try again.')
    }
  }, [])

  const retakeSelfie = () => {
    setSelfieImage(null)
    setSelfieFile(null)
  }

  // Stop camera when leaving selfie step
  useEffect(() => {
    if (step !== 'selfie' && showCamera) {
      stopCamera()
    }
  }, [step, showCamera])

  // ============================================
  // Submit KYC Data
  // ============================================

  const submitKycData = async () => {
    if (!selfieFile) {
      toast.error('Please capture or upload a selfie first')
      return
    }

    setStep('submitting')

    try {
      const submitData = new FormData()
      submitData.append('documentType', formData.documentType)
      submitData.append('idNumber', formData.idNumber.trim())
      submitData.append('firstName', formData.firstName.trim())
      submitData.append('lastName', formData.lastName.trim())
      submitData.append('dateOfBirth', formData.dateOfBirth)
      submitData.append('address', formData.address.trim() || 'N/A')
      submitData.append('city', formData.city.trim() || 'N/A')
      submitData.append('postalCode', formData.postalCode.trim() || '000000')
      submitData.append('placeOfBirth', formData.placeOfBirth.trim() || 'Nigeria')
      submitData.append('country', 'Nigeria')
      submitData.append('isSubjectConsent', 'true')

      // Add selfie file
      submitData.append('imageSelfie', selfieFile)

      const result = await submitNigerianKyc(submitData).unwrap()

      setVerificationResult(result)

      if (result.verificationResult.status === 'approved') {
        setStep('success')
        toast.success('Identity verified successfully!')
      } else if (result.verificationResult.status === 'rejected') {
        setStep('error')
        setErrorMessage(result.verificationResult.message || 'Verification failed')
      } else {
        // Pending
        setStep('success')
        toast.success('Verification submitted for review')
      }
    } catch (err: any) {
      console.error('KYC submission error:', err)
      setStep('error')
      setErrorMessage(err?.data?.message || err?.message || 'Failed to verify identity')
      toast.error('Verification failed')
    }
  }

  // ============================================
  // Render Helpers
  // ============================================

  const selectedDocType = DOCUMENT_TYPES.find(d => d.value === formData.documentType)

  // ============================================
  // Step: Intro
  // ============================================

  if (step === 'intro') {
    return (
      <div className='mt-4'>
        {/* Header */}
        <div className='text-center mb-8'>
          <div className='w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4'>
            <FiShield className='w-8 h-8 text-primary' />
          </div>
          <h2 className='text-xl font-bold text-white mb-2'>Verify Your Nigerian Identity</h2>
          <p className='text-gray-400 text-sm max-w-md mx-auto'>
            Complete verification in 2 simple steps to access all features.
          </p>
        </div>

        {/* Steps Overview */}
        <div className='bg-gray-700/30 border border-gray-600 rounded-lg p-4 mb-6'>
          <h4 className='font-semibold text-white text-sm mb-4'>Verification Process:</h4>
          <div className='space-y-4'>
            <div className='flex items-start gap-3'>
              <div className='w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0'>
                <FiFileText className='w-4 h-4 text-primary' />
              </div>
              <div>
                <p className='text-white font-medium'>Step 1: ID Information</p>
                <p className='text-gray-400 text-sm'>Enter your ID details for verification</p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0'>
                <FiCamera className='w-4 h-4 text-primary' />
              </div>
              <div>
                <p className='text-white font-medium'>Step 2: Face Verification</p>
                <p className='text-gray-400 text-sm'>Take a selfie photo to confirm your identity</p>
              </div>
            </div>
          </div>
        </div>

        {/* Supported IDs */}
        <div className='bg-blue-900/20 border border-blue-600/50 rounded-lg p-4 mb-8'>
          <h4 className='font-semibold text-blue-300 text-sm mb-2'>Supported ID Types:</h4>
          <div className='flex flex-wrap gap-2'>
            {['NIN', 'BVN', 'vNIN', "Driver's License", 'Passport', 'PVC'].map(id => (
              <span key={id} className='bg-blue-900/50 text-blue-200 text-xs px-2 py-1 rounded'>
                {id}
              </span>
            ))}
          </div>
        </div>

        {/* Start Button */}
        <button
          type='button'
          className='btn-primary w-full py-4 rounded-lg flex items-center justify-center gap-3 text-lg font-semibold'
          onClick={() => setStep('idForm')}
        >
          Start Verification
          <FiArrowRight className='w-5 h-5' />
        </button>

        {/* Privacy Note */}
        <p className='text-xs text-gray-400 text-center mt-6'>
          Your information is securely processed. By proceeding, you agree to the verification of your identity.
        </p>
      </div>
    )
  }

  // ============================================
  // Step: ID Form
  // ============================================

  if (step === 'idForm') {
    return (
      <div className='mt-4'>
        {/* Progress */}
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-2'>
            <div className='w-8 h-8 bg-primary rounded-full flex items-center justify-center'>
              <span className='text-black font-semibold text-sm'>1</span>
            </div>
            <div className='w-16 h-1 bg-gray-600 rounded'></div>
            <div className='w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center'>
              <span className='text-gray-400 font-semibold text-sm'>2</span>
            </div>
          </div>
          <span className='text-gray-400 text-sm'>Step 1 of 2</span>
        </div>

        <h3 className='text-lg font-semibold text-white mb-4 flex items-center gap-2'>
          <FiFileText className='w-5 h-5 text-primary' />
          ID Information
        </h3>

        <form className='space-y-4' onSubmit={e => { e.preventDefault(); handleContinueToSelfie(); }}>
          {/* Document Type */}
          <div>
            <label className='block text-sm text-gray-300 mb-1'>Document Type *</label>
            <select
              className={`form-input ${errors.documentType ? 'border-red-500' : ''}`}
              value={formData.documentType}
              onChange={e => handleInputChange('documentType', e.target.value)}
            >
              <option value=''>Select document type...</option>
              {DOCUMENT_TYPES.map(doc => (
                <option key={doc.value} value={doc.value}>{doc.label}</option>
              ))}
            </select>
            {errors.documentType && <p className='text-red-400 text-xs mt-1'>{errors.documentType}</p>}
          </div>

          {/* ID Number */}
          <div>
            <label className='block text-sm text-gray-300 mb-1'>ID Number *</label>
            <input
              type='text'
              className={`form-input ${errors.idNumber ? 'border-red-500' : ''}`}
              placeholder={selectedDocType?.hint || 'Enter your ID number'}
              value={formData.idNumber}
              onChange={e => handleInputChange('idNumber', e.target.value.toUpperCase())}
            />
            {selectedDocType && (
              <p className='text-gray-300 text-xs mt-1'>Format: {selectedDocType.hint}</p>
            )}
            {errors.idNumber && <p className='text-red-400 text-xs mt-1'>{errors.idNumber}</p>}
          </div>

          {/* Personal Details Section */}
          <div className='border-t border-gray-600 pt-4 mt-4'>
            <h4 className='text-sm font-medium text-gray-300 mb-1 flex items-center gap-2'>
              <FiUser className='w-4 h-4' />
              Personal Details
            </h4>
            <p className='text-xs text-gray-300 mb-3'>
              Enter your names exactly as they appear on your NIN / BVN / ID.
              Your surname is your family name.
            </p>

            <div className='grid grid-cols-2 gap-3'>
              {/* First Name */}
              <div>
                <label className='block text-sm text-gray-300 mb-1'>First Name *</label>
                <input
                  type='text'
                  className={`form-input ${errors.firstName ? 'border-red-500' : ''}`}
                  placeholder='e.g. Babatunde'
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                />
                {errors.firstName ? (
                  <p className='text-red-400 text-xs mt-1'>{errors.firstName}</p>
                ) : (
                  <p className='text-gray-300 text-xs mt-1'>Given / first name</p>
                )}
              </div>

              {/* Surname */}
              <div>
                <label className='block text-sm text-gray-300 mb-1'>Surname *</label>
                <input
                  type='text'
                  className={`form-input ${errors.lastName ? 'border-red-500' : ''}`}
                  placeholder='e.g. Adeyemi'
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                />
                {errors.lastName ? (
                  <p className='text-red-400 text-xs mt-1'>{errors.lastName}</p>
                ) : (
                  <p className='text-gray-300 text-xs mt-1'>Family name / surname</p>
                )}
              </div>
            </div>

            {/* Date of Birth */}
            <div className='mt-3'>
              <label className='block text-sm text-gray-300 mb-1'>Date of Birth *</label>
              <input
                type='date'
                className={`form-input ${errors.dateOfBirth ? 'border-red-500' : ''}`}
                value={formData.dateOfBirth}
                onChange={e => handleInputChange('dateOfBirth', e.target.value)}
              />
              {errors.dateOfBirth && <p className='text-red-400 text-xs mt-1'>{errors.dateOfBirth}</p>}
            </div>
          </div>

          {/* Address Section */}
          <div className='border-t border-gray-600 pt-4 mt-4'>
            <h4 className='text-sm font-medium text-gray-300 mb-3'>Address (Optional)</h4>

            <div className='space-y-3'>
              <div>
                <label className='block text-sm text-gray-300 mb-1'>Street Address</label>
                <input
                  type='text'
                  className='form-input'
                  placeholder='123 Main Street'
                  value={formData.address}
                  onChange={e => handleInputChange('address', e.target.value)}
                />
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm text-gray-300 mb-1'>City</label>
                  <input
                    type='text'
                    className='form-input'
                    placeholder='Lagos'
                    value={formData.city}
                    onChange={e => handleInputChange('city', e.target.value)}
                  />
                </div>
                <div>
                  <label className='block text-sm text-gray-300 mb-1'>Postal Code</label>
                  <input
                    type='text'
                    className='form-input'
                    placeholder='100001'
                    value={formData.postalCode}
                    onChange={e => handleInputChange('postalCode', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm text-gray-300 mb-1'>Place of Birth</label>
                <input
                  type='text'
                  className='form-input'
                  placeholder='City/State of birth'
                  value={formData.placeOfBirth}
                  onChange={e => handleInputChange('placeOfBirth', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className='border-t border-gray-600 pt-4 mt-4'>
            <label className='flex items-start gap-3 cursor-pointer'>
              <input
                type='checkbox'
                className='form-checkbox mt-0.5'
                checked={formData.consent}
                onChange={e => handleInputChange('consent', e.target.checked)}
              />
              <span className='text-sm text-gray-300'>
                I consent to the verification of my identity using the provided information. I confirm that the details are accurate and belong to me.
              </span>
            </label>
            {errors.consent && <p className='text-red-400 text-xs mt-1'>{errors.consent}</p>}
          </div>

          {/* Buttons */}
          <div className='flex gap-3 pt-4'>
            <button
              type='button'
              className='btn-secondary py-3 px-4 rounded-lg flex items-center gap-2'
              onClick={() => setStep('intro')}
            >
              <FiArrowLeft className='w-4 h-4' />
              Back
            </button>
            <button
              type='submit'
              className='btn-primary flex-1 py-3 rounded-lg flex items-center justify-center gap-2'
            >
              Continue to Selfie
              <FiArrowRight className='w-4 h-4' />
            </button>
          </div>
        </form>

        {/* Sandbox Hint */}
        {sandboxMode && (
          <div className='mt-4 p-3 bg-yellow-900/20 border border-yellow-600/50 rounded-lg'>
            <p className='text-yellow-400 text-xs font-medium mb-1'>Sandbox Test IDs:</p>
            <p className='text-yellow-300/70 text-xs'>
              BVN/NIN: 11111111111 | vNIN: YV111111111111FY | Passport: A11111111
            </p>
          </div>
        )}
      </div>
    )
  }

  // ============================================
  // Step: Selfie
  // ============================================

  if (step === 'selfie') {
    return (
      <div className='mt-4'>
        {/* Progress */}
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-2'>
            <div className='w-8 h-8 bg-green-500 rounded-full flex items-center justify-center'>
              <FiCheckCircle className='w-4 h-4 text-white' />
            </div>
            <div className='w-16 h-1 bg-primary rounded'></div>
            <div className='w-8 h-8 bg-primary rounded-full flex items-center justify-center'>
              <span className='text-black font-semibold text-sm'>2</span>
            </div>
          </div>
          <span className='text-gray-400 text-sm'>Step 2 of 2</span>
        </div>

        <div className='text-center mb-6'>
          <div className='w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4'>
            <FiCamera className='w-8 h-8 text-primary' />
          </div>
          <h3 className='text-lg font-semibold text-white mb-2'>Take a Selfie</h3>
          <p className='text-gray-400 text-sm max-w-md mx-auto'>
            Your selfie will be compared with the photo on your{' '}
            <span className='text-primary font-medium'>
              {selectedDocType?.label.split(' ')[0] || 'ID'}
            </span>{' '}
            record to confirm your identity.
          </p>
        </div>

        {/* ID Summary */}
        <div className='bg-gray-700/30 border border-gray-600 rounded-lg p-4 mb-6'>
          <h4 className='text-sm font-medium text-gray-300 mb-2'>Verifying:</h4>
          <div className='space-y-1'>
            <p className='text-white text-sm'>
              <span className='text-gray-400'>Name:</span> {formData.firstName} {formData.lastName}
            </p>
            <p className='text-white text-sm'>
              <span className='text-gray-400'>Document:</span> {selectedDocType?.label}
            </p>
            <p className='text-white text-sm font-mono'>
              <span className='text-gray-400'>ID Number:</span> {formData.idNumber}
            </p>
          </div>
        </div>

        {/* Selfie Capture Area */}
        <div className='flex flex-col gap-4 mb-6'>
          {showCamera ? (
            <div className='flex flex-col gap-4'>
              <div className='relative mx-auto w-80 h-64 bg-black rounded-lg overflow-hidden'>
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  width={320}
                  height={256}
                  screenshotFormat='image/jpeg'
                  videoConstraints={{
                    width: 320,
                    height: 256,
                    facingMode: 'user',
                  }}
                  className='w-full h-full object-cover'
                  style={{ transform: 'scaleX(-1)' }}
                />
                <div className='absolute inset-0 border-4 border-white/30 rounded-lg pointer-events-none'>
                  <div className='absolute top-4 left-4 right-4 h-8 bg-black/50 rounded flex items-center justify-center'>
                    <p className='text-white text-sm'>Position your face in the center</p>
                  </div>
                </div>
              </div>
              <div className='flex gap-4 justify-center'>
                <button
                  type='button'
                  className='btn-secondary py-2 px-4 rounded-md'
                  onClick={stopCamera}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn-primary py-2 px-4 rounded-md flex items-center gap-2'
                  onClick={captureSelfie}
                >
                  <FiCamera className='w-4 h-4' />
                  Capture
                </button>
              </div>
            </div>
          ) : selfieImage ? (
            <div className='flex flex-col items-center gap-4'>
              <div className='relative mx-auto w-64 h-64 rounded-lg overflow-hidden'>
                <img
                  src={selfieImage}
                  alt='selfie preview'
                  className='w-full h-full object-cover rounded-lg'
                />
              </div>
              <button
                type='button'
                className='text-primary text-sm flex items-center gap-2 hover:underline'
                onClick={retakeSelfie}
              >
                <FiCamera className='w-4 h-4' />
                Retake photo
              </button>
            </div>
          ) : (
            <div className='flex flex-col items-center gap-4'>
              <div
                className='h-64 w-64 bg-gray-600 flex justify-center items-center flex-col cursor-pointer mx-auto rounded-lg hover:bg-gray-500/80 transition-colors'
                onClick={startCamera}
              >
                <FiCamera className='text-white/60 text-4xl' />
                <p className='text-xs text-center text-gray-200 mt-2'>Tap to open camera</p>
              </div>
            </div>
          )}
        </div>

        {/* Tips */}
        <div className='bg-blue-900/20 border border-blue-600/50 rounded-lg p-4 mb-6'>
          <h4 className='font-semibold text-blue-300 text-sm mb-2'>Tips for best results:</h4>
          <ul className='space-y-1 text-sm text-blue-200/80'>
            <li className='flex items-center gap-2'>
              <FiCheckCircle className='w-3 h-3 text-blue-400' />
              Ensure good lighting on your face
            </li>
            <li className='flex items-center gap-2'>
              <FiCheckCircle className='w-3 h-3 text-blue-400' />
              Remove glasses, hats, or face coverings
            </li>
            <li className='flex items-center gap-2'>
              <FiCheckCircle className='w-3 h-3 text-blue-400' />
              Look directly at the camera
            </li>
          </ul>
        </div>

        {/* Submit / Back buttons */}
        <div className='flex gap-3'>
          <button
            type='button'
            className='btn-secondary py-3 px-4 rounded-lg flex items-center gap-2'
            onClick={() => setStep('idForm')}
          >
            <FiArrowLeft className='w-4 h-4' />
            Back
          </button>
          <button
            type='button'
            className='btn-primary flex-1 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={!selfieFile}
            onClick={submitKycData}
          >
            Submit Verification
            <FiArrowRight className='w-4 h-4' />
          </button>
        </div>
      </div>
    )
  }

  // ============================================
  // Step: Submitting
  // ============================================

  if (step === 'submitting') {
    return (
      <div className='mt-4 flex flex-col items-center justify-center py-12'>
        <div className='w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4'>
          <FiLoader className='w-8 h-8 text-primary animate-spin' />
        </div>
        <h3 className='text-lg font-semibold text-white mb-2'>Verifying Your Identity</h3>
        <p className='text-gray-400 text-sm text-center max-w-md'>
          Please wait while we verify your ID information against government records...
        </p>
        <div className='mt-6 space-y-2'>
          <div className='flex items-center gap-2 text-sm text-gray-400'>
            <FiCheckCircle className='w-4 h-4 text-green-400' />
            Selfie uploaded
          </div>
          <div className='flex items-center gap-2 text-sm text-gray-400'>
            <FiLoader className='w-4 h-4 text-primary animate-spin' />
            Verifying ID with government database...
          </div>
        </div>
      </div>
    )
  }

  // ============================================
  // Step: Success
  // ============================================

  if (step === 'success') {
    const result = verificationResult?.verificationResult
    const isApproved = result?.status === 'approved'
    const isPending = result?.status === 'pending'

    return (
      <div className='mt-4'>
        <div className='text-center mb-6'>
          <div className={`w-16 h-16 ${isApproved ? 'bg-green-500/20' : 'bg-yellow-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isApproved ? (
              <FiCheckCircle className='w-8 h-8 text-green-400' />
            ) : (
              <FiInfo className='w-8 h-8 text-yellow-400' />
            )}
          </div>
          <h3 className='text-lg font-semibold text-white mb-2'>
            {isApproved ? 'Verification Complete!' : 'Verification Submitted'}
          </h3>
          <p className='text-gray-400 text-sm'>
            {isApproved
              ? 'Your identity has been successfully verified.'
              : 'Your verification is being processed.'}
          </p>
        </div>

        {/* Result Details */}
        <div className={`${isApproved ? 'bg-green-900/20 border-green-600/50' : 'bg-yellow-900/20 border-yellow-600/50'} border rounded-lg p-4 mb-6`}>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <span className='text-gray-400 text-sm'>Status</span>
              <span className={`font-medium ${isApproved ? 'text-green-400' : 'text-yellow-400'}`}>
                {isApproved ? 'Approved' : isPending ? 'Pending Review' : result?.status}
              </span>
            </div>
            {result?.details?.dataMatched !== undefined && (
              <div className='flex items-center justify-between'>
                <span className='text-gray-400 text-sm'>ID Data Match</span>
                <span className={result.details.dataMatched ? 'text-green-400' : 'text-red-400'}>
                  {result.details.dataMatched ? 'Passed' : 'Failed'}
                </span>
              </div>
            )}
            {result?.details?.selfieMatched !== undefined && (
              <div className='flex items-center justify-between'>
                <span className='text-gray-400 text-sm'>Face Match</span>
                <span className={result.details.selfieMatched ? 'text-green-400' : 'text-red-400'}>
                  {result.details.selfieMatched
                    ? `Passed (${result.details.selfieConfidence || 0}%)`
                    : 'Failed'}
                </span>
              </div>
            )}
            <div className='flex items-center justify-between'>
              <span className='text-gray-400 text-sm'>Name</span>
              <span className='text-white'>{formData.firstName} {formData.lastName}</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-gray-400 text-sm'>Document Type</span>
              <span className='text-white'>{selectedDocType?.label.split(' ')[0]}</span>
            </div>
          </div>
        </div>

        {result?.message && (
          <p className='text-gray-400 text-sm text-center mb-6'>{result.message}</p>
        )}

        {/* Done Button */}
        <button
          type='button'
          className='btn-primary w-full py-4 rounded-lg font-semibold'
          onClick={() => setCurrentStep(isApproved ? 'Verified' : 'Success')}
        >
          {isApproved ? 'Continue' : 'Done'}
        </button>
      </div>
    )
  }

  // ============================================
  // Step: Error
  // ============================================

  if (step === 'error') {
    return (
      <div className='mt-4'>
        <div className='text-center mb-6'>
          <div className='w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4'>
            <FiAlertCircle className='w-8 h-8 text-red-400' />
          </div>
          <h3 className='text-lg font-semibold text-white mb-2'>Verification Failed</h3>
          <p className='text-gray-400 text-sm'>{errorMessage || 'Unable to verify your identity.'}</p>
        </div>

        {/* Error Details */}
        <div className='bg-red-900/20 border border-red-600/50 rounded-lg p-4 mb-6'>
          <h4 className='text-sm font-medium text-red-300 mb-2'>What you can try:</h4>
          <ul className='space-y-1 text-sm text-red-200/80'>
            <li className='flex items-start gap-2'>
              <span className='text-red-400'>*</span>
              Double-check your ID number is correct
            </li>
            <li className='flex items-start gap-2'>
              <span className='text-red-400'>*</span>
              Ensure your personal details match your ID exactly
            </li>
            <li className='flex items-start gap-2'>
              <span className='text-red-400'>*</span>
              Try a different document type if available
            </li>
          </ul>
        </div>

        {/* Retry Buttons */}
        <div className='flex gap-3'>
          <button
            type='button'
            className='btn-secondary flex-1 py-3 rounded-lg'
            onClick={() => setCurrentStep('init')}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn-primary flex-1 py-3 rounded-lg'
            onClick={() => {
              retakeSelfie()
              setStep('idForm')
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return null
}

export default NigerianKycForm
